import { SlackWorkspaceConnection } from './SlackWorkspaceConnection'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CalendarDays, Check, ChevronDown, Clock3, Hash, Save, Users } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'

export interface ScheduleConfig {
  enabled: boolean
  dm_time: string
  timezone: string
  standup_channel: string | null
  days_of_week: number[]
  ai_summary_enabled: boolean
  ai_summary_delay_hours: number
  digest_enabled: boolean
  digest_time: string
  digest_channel: string | null
}

interface ScheduleConfigurationProps {
  teamId: string
  userRole: 'owner' | 'admin' | 'member' | 'manager'
  onSavingChange?: (saving: boolean) => void
  onSaved?: (config: ScheduleConfig) => void
}

interface SlackChannel {
  id: string
  name: string
  is_member: boolean
  is_private: boolean
}

export const DEFAULT_SCHEDULE_CONFIG: ScheduleConfig = {
  enabled: false,
  dm_time: '09:00',
  timezone: 'UTC',
  standup_channel: null,
  days_of_week: [1, 2, 3, 4, 5],
  ai_summary_enabled: true,
  ai_summary_delay_hours: 12,
  digest_enabled: false,
  digest_time: '09:00',
  digest_channel: null,
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Asia/Kolkata',
  'America/Toronto',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
]

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
]

const getWeekendEnabled = (days: number[]) => days.includes(0) || days.includes(6)

const formatScheduleDays = (days: number[]) => {
  const selectedDays = DAYS_OF_WEEK.filter((day) => days.includes(day.value)).map((day) => day.label)
  if (selectedDays.length === 0) return 'No days selected'
  if (selectedDays.length === 7) return 'Every day'
  if (selectedDays.length === 2 && selectedDays.includes('Saturday') && selectedDays.includes('Sunday')) return 'Saturday and Sunday'

  return selectedDays.join(', ')
}

const TIME_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'))
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))

const getTimeParts = (time: string) => {
  const [rawHours, rawMinutes] = time.split(':')
  const hours = Number.parseInt(rawHours, 10)
  const minutes = Number.parseInt(rawMinutes, 10)
  const normalizedHours = Number.isFinite(hours) && hours >= 0 && hours <= 23 ? hours : 9
  const normalizedMinutes = Number.isFinite(minutes) && minutes >= 0 && minutes <= 59 ? minutes : 0

  return {
    hour: String(normalizedHours % 12 || 12).padStart(2, '0'),
    minute: String(normalizedMinutes).padStart(2, '0'),
    period: normalizedHours >= 12 ? 'PM' : 'AM',
  }
}

const updateTimePart = (time: string, part: 'hour' | 'minute' | 'period', value: string) => {
  const current = getTimeParts(time)
  const next = { ...current, [part]: value }
  const hour = Number.parseInt(next.hour, 10) % 12
  const hours24 = next.period === 'PM' ? hour + 12 : hour
  return `${String(hours24).padStart(2, '0')}:${next.minute}`
}

interface TimeSelectProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange, disabled }) => {
  const parts = getTimeParts(value)
  const updatePart = (part: 'hour' | 'minute' | 'period') => (nextValue: string) => onChange(updateTimePart(value, part, nextValue))

  return (
    <div className="flex items-center gap-2" aria-label="Check-in time">
      <Select value={parts.hour} onValueChange={updatePart('hour')} disabled={disabled}>
        <SelectTrigger aria-label="Check-in hour" className="w-20"><SelectValue /></SelectTrigger>
        <SelectContent>{TIME_HOURS.map((hour) => <SelectItem key={hour} value={hour}>{hour}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-sm font-medium text-muted-foreground">:</span>
      <Select value={parts.minute} onValueChange={updatePart('minute')} disabled={disabled}>
        <SelectTrigger aria-label="Check-in minute" className="w-20"><SelectValue /></SelectTrigger>
        <SelectContent>{TIME_MINUTES.map((minute) => <SelectItem key={minute} value={minute}>{minute}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={parts.period} onValueChange={updatePart('period')} disabled={disabled}>
        <SelectTrigger aria-label="Check-in period" className="w-20"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
      </Select>
    </div>
  )
}

export const ScheduleConfiguration: React.FC<ScheduleConfigurationProps> = ({ teamId, userRole, onSavingChange, onSaved }) => {
  const [slackConnected, setSlackConnected] = useState<boolean | null>(null)
  const [config, setConfig] = useState<ScheduleConfig>(DEFAULT_SCHEDULE_CONFIG)
  const loadedConfigRef = useRef<ScheduleConfig>(DEFAULT_SCHEDULE_CONFIG)
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  useEffect(() => { onSavingChange?.(saving); return () => onSavingChange?.(false) }, [saving, onSavingChange])
  // ROLE AUDIT (2026-10-09): admin-only, not admin+manager. This configures
  // the team-wide default check-in time/channel/days/AI-summary/digest --
  // a single shared setting, not a pod-scoped one. Per-pod overrides are the
  // manager-accessible path, handled separately by PodStandupOverrides.tsx
  // (mounted by DeliveryConfiguration.tsx, which does its own manager gating).
  const isAdmin = userRole === 'admin' || userRole === 'owner'

  const timezoneOptions = useMemo(
    () => (TIMEZONES.includes(config.timezone) ? TIMEZONES : [config.timezone, ...TIMEZONES]),
    [config.timezone],
  )

  useEffect(() => {
    let cancelled = false

    const loadConfiguration = async () => {
      try {
        const { data, error: loadError } = await supabase
          .from('teams')
          .select('standup_config')
          .eq('id', teamId)
          .single() as { data: { standup_config?: Partial<ScheduleConfig> | null } | null; error: any }

        if (loadError) throw loadError
        if (!cancelled && data?.standup_config) {
          const loadedConfig = { ...DEFAULT_SCHEDULE_CONFIG, ...data.standup_config }
          loadedConfigRef.current = loadedConfig
          setConfig(loadedConfig)
        }
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Failed to load configuration')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadConfiguration()
    return () => {
      cancelled = true
    }
  }, [teamId])

  useEffect(() => {
    if (!isAdmin) return

    const loadSlackChannels = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        if (!sessionData.session) return

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-channels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
          body: JSON.stringify({ team_id: teamId }),
        })

        if (response.ok) {
          const data = await response.json()
          setChannels(data.channels || [])
        }
      } catch (loadError) {
        console.error('Failed to load Slack channels:', loadError)
      }
    }

    void loadSlackChannels()
  }, [isAdmin, teamId])

  const updateConfig = <K extends keyof ScheduleConfig>(key: K, value: ScheduleConfig[K]) => {
    setConfig((previous) => ({ ...previous, [key]: value }))
    setSuccess('')
  }

  const handleWeekendToggle = (checked: boolean) => {
    const withoutWeekend = config.days_of_week.filter((day) => day !== 0 && day !== 6)
    updateConfig('days_of_week', checked ? [...withoutWeekend, 6, 0] : withoutWeekend)
  }

  const handleDayToggle = (day: number) => {
    const days = config.days_of_week.includes(day)
      ? config.days_of_week.filter((selectedDay) => selectedDay !== day)
      : [...config.days_of_week, day]
    updateConfig('days_of_week', days.sort((a, b) => a - b))
  }

  const loadSlackChannels = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData.session) return
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify({ team_id: teamId }),
      })
      if (response.ok) setChannels((await response.json()).channels || [])
    } catch (loadError) {
      console.error('Failed to refresh Slack channels:', loadError)
    }
  }

  const handleSave = async () => {
    if ((config.enabled || config.digest_enabled) && slackConnected !== true) {
      setError('Connect Slack before enabling reminders or summaries. Other changes can be saved with both features off.')
      return
    }
    if (!isAdmin) return
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      // Re-fetch and merge on top of whatever's on the server right now,
      // rather than blind-overwriting standup_config with our possibly-stale
      // local state. DeliveryConfiguration.tsx writes the same JSON column
      // (overlapping digest_* keys) from a separate tab, so a plain
      // `.update({ standup_config: config })` here could silently revert a
      // concurrent Delivery-tab save made after this component last loaded.
      const { data: currentTeam, error: loadError } = await supabase
        .from('teams')
        .select('standup_config')
        .eq('id', teamId)
        .single() as { data: { standup_config?: Record<string, unknown> | null } | null; error: any }

      if (loadError) throw loadError

      const localChanges = Object.fromEntries(
        (Object.keys(config) as Array<keyof ScheduleConfig>)
          .filter((key) => JSON.stringify(config[key]) !== JSON.stringify(loadedConfigRef.current[key]))
          .map((key) => [key, config[key]]),
      )

      const mergedConfig = { ...(currentTeam?.standup_config ?? {}), ...localChanges }
      const { error: saveError } = await supabase
        .from('teams')
        .update({ standup_config: mergedConfig } as any)
        .eq('id', teamId)

      if (saveError) throw saveError
      loadedConfigRef.current = { ...DEFAULT_SCHEDULE_CONFIG, ...mergedConfig }
      setConfig((previous) => ({ ...previous, ...mergedConfig }))
      setSuccess('Schedule saved successfully.')
      onSaved?.({ ...DEFAULT_SCHEDULE_CONFIG, ...mergedConfig })
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-background">
        <div className="space-y-3 border-b border-border p-4 sm:p-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center justify-between gap-6 border-b border-border p-4 sm:p-6">
            <div className="space-y-2"><Skeleton className="h-5 w-40" /><Skeleton className="h-4 w-64 max-w-full" /></div>
            <Skeleton className="h-10 w-36" />
          </div>
        ))}
        <div className="flex justify-end p-4 sm:p-6"><Skeleton className="h-10 w-32" /></div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">Standup schedule</h2>
        <p className="mt-1 text-sm text-muted-foreground">Manage when connectors and humans are expected to check in</p>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-5 py-3 text-sm text-destructive sm:px-6">
          <AlertCircle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}
      {success && (
        <div role="status" className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-5 py-3 text-sm text-foreground sm:px-6">
          <Check className="h-4 w-4 shrink-0" />{success}
        </div>
      )}

      <div className="divide-y divide-border">
        <div className="flex items-center justify-between gap-6 p-4 sm:p-6">
          <div>
            <p className="font-medium text-foreground">Enable proactive standups</p>
            <p className="mt-1 text-sm text-muted-foreground">Optional: send Slack reminders to your team. Changes take effect when you save.</p>
          </div>
          <Switch checked={config.enabled} onCheckedChange={(checked) => updateConfig('enabled', checked)} disabled={!isAdmin} aria-label="Enable proactive standups" />
        </div>
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Clock3 className="h-4 w-4" />Check-in time</div>
            <p className="mt-1 text-sm text-muted-foreground">Check-ins use the configured reminder time.</p>
          </div>
          <TimeSelect value={config.dm_time} onChange={(value) => updateConfig('dm_time', value)} disabled={!isAdmin} />
        </div>

        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><CalendarDays className="h-4 w-4" />Timezone</div>
            <p className="mt-1 text-sm text-muted-foreground">Applies to reminders and the schedule above.</p>
          </div>
          <Select value={config.timezone} onValueChange={(value) => updateConfig('timezone', value)} disabled={!isAdmin}>
            <SelectTrigger aria-label="Timezone" className="w-full sm:w-52"><SelectValue /></SelectTrigger>
            <SelectContent>{timezoneOptions.map((timezone) => <SelectItem key={timezone} value={timezone}>{timezone}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between gap-6 p-4 sm:p-6">
          <div>
            <div className="text-sm font-medium text-foreground">Weekend check-ins</div>
            <p className="mt-1 text-sm text-muted-foreground">Require standups on Saturday and Sunday</p>
          </div>
          <Switch checked={getWeekendEnabled(config.days_of_week)} onCheckedChange={handleWeekendToggle} disabled={!isAdmin} aria-label="Weekend check-ins" />
        </div>
      </div>

      {isAdmin && <div className="p-4 sm:p-6"><SlackWorkspaceConnection teamId={teamId} onStatusChange={setSlackConnected} canInstall={userRole === 'owner'} /></div>}
      <details open={advancedOpen} onToggle={(event) => setAdvancedOpen(event.currentTarget.open)} className="border-t border-border">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 text-sm font-medium text-foreground sm:p-6">
          <span className="flex items-center gap-2"><ChevronDown className="h-4 w-4 transition-transform" />Advanced schedule settings</span>
          <span className="text-xs font-normal text-muted-foreground">Channels, weekdays, summaries</span>
        </summary>
        <div className="space-y-6 border-t border-border p-4 sm:p-6">


          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground"><Hash className="h-4 w-4" />Standup channel</label>
            <div className="flex gap-2"><Select value={config.standup_channel || 'none'} onValueChange={(value) => updateConfig('standup_channel', value === 'none' ? null : value)} disabled={!isAdmin}><SelectTrigger className="flex-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No channel (DMs only)</SelectItem>{channels.map((channel) => <SelectItem key={channel.id} value={channel.id}>#{channel.name}{channel.is_private ? ' 🔒' : ''}</SelectItem>)}</SelectContent></Select><Button type="button" variant="outline" onClick={loadSlackChannels} disabled={!isAdmin}>Refresh</Button></div>
            <p className="text-xs text-muted-foreground">Private channels appear after inviting SyncSimple with <code>/invite @SyncSimple</code>.</p>
          </div>

          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground"><CalendarDays className="h-4 w-4" />Schedule days</p>
            <p className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              {config.days_of_week.length === 0
                ? 'No proactive standups will be sent because no days are selected.'
                : `Runs ${formatScheduleDays(config.days_of_week)} at ${getTimeParts(config.dm_time).hour}:${getTimeParts(config.dm_time).minute} ${getTimeParts(config.dm_time).period} (${config.timezone}).`}
            </p>
            <div className="flex flex-wrap gap-2">{DAYS_OF_WEEK.map((day) => <Button key={day.value} type="button" size="sm" variant={config.days_of_week.includes(day.value) ? 'default' : 'outline'} onClick={() => handleDayToggle(day.value)} disabled={!isAdmin}>{day.label}</Button>)}</div>
          </div>

          <div className="grid gap-6 border-t border-border pt-6 md:grid-cols-2">
            <div className="flex items-center justify-between gap-4"><div><p className="font-medium text-foreground">AI summary</p><p className="text-sm text-muted-foreground">Generate a daily standup summary.</p></div><Switch checked={config.ai_summary_enabled} onCheckedChange={(checked) => updateConfig('ai_summary_enabled', checked)} disabled={!isAdmin} aria-label="AI summary" /></div>
            <div className="space-y-2"><label className="text-sm font-medium text-foreground" htmlFor="summary-delay">Summary delay (hours)</label><Input id="summary-delay" type="number" min="1" max="24" value={config.ai_summary_delay_hours} onChange={(event) => updateConfig('ai_summary_delay_hours', Number.parseInt(event.target.value, 10) || 1)} disabled={!isAdmin} /></div>
          </div>

          <div className="space-y-4 border-t border-border pt-6">
            <div className="flex items-center justify-between gap-4"><div><p className="font-medium text-foreground">Daily channel digest</p><p className="text-sm text-muted-foreground">Post a combined standup and connector activity summary.</p></div><Switch checked={config.digest_enabled} onCheckedChange={(checked) => updateConfig('digest_enabled', checked)} disabled={!isAdmin} aria-label="Daily channel digest" /></div>
            {config.digest_enabled && <div className="grid gap-4 md:grid-cols-2"><div className="space-y-2"><label className="text-sm font-medium text-foreground" htmlFor="digest-time">Digest time</label><Input id="digest-time" type="time" value={config.digest_time} onChange={(event) => updateConfig('digest_time', event.target.value)} disabled={!isAdmin} /></div><div className="space-y-2"><label className="flex items-center gap-2 text-sm font-medium text-foreground"><Hash className="h-4 w-4" />Digest channel</label><Select value={config.digest_channel || 'none'} onValueChange={(value) => updateConfig('digest_channel', value === 'none' ? null : value)} disabled={!isAdmin}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Select a channel...</SelectItem>{channels.map((channel) => <SelectItem key={channel.id} value={channel.id}>#{channel.name}{channel.is_private ? ' 🔒' : ''}</SelectItem>)}</SelectContent></Select></div></div>}
          </div>
        </div>
      </details>

      <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        {!isAdmin && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4" />Only team admins can modify schedule settings.</p>}
        <Button onClick={handleSave} disabled={!isAdmin || saving || ((config.enabled || config.digest_enabled) && slackConnected !== true)} className="ml-auto min-w-32 bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? <><span className="animate-spin rounded-full border-2 border-current border-b-transparent h-4 w-4" />Saving...</> : <><Save className="h-4 w-4" />Save Changes</>}
        </Button>
      </div>
    </div>
  )
}
